import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { DataGridPro, GridColDef, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { ConfirmationDialog } from "../../pricing-management/pages/deals-page/delete-dilog.tsx";
import AdminAPI, { AdminUser, Organization, Team } from "../api/admin-api.ts";

const NO_TEAM = "";
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function UsersPage() {
  const { getAccessTokenSilently } = useAuth0();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackMessage, setSnackMessage] = useState<string | undefined>(undefined);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [canExtractPdf, setCanExtractPdf] = useState(false);
  const [organizationId, setOrganizationId] = useState("");
  const [teamId, setTeamId] = useState(NO_TEAM);

  const [blockTarget, setBlockTarget] = useState<AdminUser | null>(null);

  const organizationNames = new Map(organizations.map((organization) => [organization.id, organization.name]));
  const teamNames = new Map(teams.map((team) => [team.id, team.name]));
  const organizationTeams = teams.filter((team) => team.organization_id === organizationId);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = await getAccessTokenSilently();
    const [usersResult, organizationsResult, teamsResult] = await Promise.all([
      AdminAPI.getUsers(token),
      AdminAPI.getOrganizations(token),
      AdminAPI.getTeams(token),
    ]);
    setLoading(false);
    if (!usersResult.valid) return setSnackMessage(usersResult.error);
    if (!organizationsResult.valid) return setSnackMessage(organizationsResult.error);
    if (!teamsResult.valid) return setSnackMessage(teamsResult.error);
    setUsers(usersResult.body);
    setOrganizations(organizationsResult.body);
    setTeams(teamsResult.body);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateDialog = () => {
    setEditing(null);
    setEmail("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setIsAdmin(false);
    setCanExtractPdf(false);
    setOrganizationId("");
    setTeamId(NO_TEAM);
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminUser) => {
    setEditing(user);
    setEmail(user.email ?? "");
    setPassword("");
    setFirstName(user.first_name ?? "");
    setLastName(user.last_name ?? "");
    setIsAdmin(user.is_admin ?? false);
    setCanExtractPdf(user.can_extract_pdf ?? false);
    setOrganizationId(user.organization_id);
    setTeamId(user.team_id ?? NO_TEAM);
    setDialogOpen(true);
  };

  // Changing the organization resets the team so it can never mismatch
  const handleOrganizationChange = (value: string) => {
    setOrganizationId(value);
    setTeamId(NO_TEAM);
  };

  const commonFieldsValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    organizationId !== "";

  const canSave = editing
    ? commonFieldsValid
    : commonFieldsValid && EMAIL_REGEX.test(email.trim()) && password.length >= 8;

  const handleSave = async () => {
    if (!canSave) return;
    const token = await getAccessTokenSilently();
    const commonFields = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      is_admin: isAdmin,
      can_extract_pdf: canExtractPdf,
      organization_id: organizationId,
      team_id: teamId === NO_TEAM ? null : teamId,
    };
    const result = editing
      ? await AdminAPI.updateUser(token, editing.id, commonFields)
      : await AdminAPI.createUser(token, { ...commonFields, email: email.trim().toLowerCase(), password });
    if (!result.valid) return setSnackMessage(result.error);
    setDialogOpen(false);
    setSnackMessage(editing ? "User updated." : "User created.");
    fetchData();
  };

  const handleBlockToggle = async () => {
    if (!blockTarget) return;
    const blocking = !blockTarget.blocked;
    const token = await getAccessTokenSilently();
    const result = await AdminAPI.setUserBlocked(token, blockTarget.id, blocking);
    setBlockTarget(null);
    if (!result.valid) return setSnackMessage(result.error);
    setSnackMessage(blocking ? "User blocked." : "User unblocked.");
    fetchData();
  };

  const columns: GridColDef[] = [
    { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
    { field: "first_name", headerName: "First Name", width: 120 },
    { field: "last_name", headerName: "Last Name", width: 120 },
    {
      field: "is_admin",
      headerName: "Admin",
      width: 80,
      valueFormatter: (value) => (value ? "Yes" : "No"),
    },
    {
      field: "can_extract_pdf",
      headerName: "PDF Import",
      width: 100,
      valueFormatter: (value) => (value ? "Yes" : "No"),
    },
    {
      field: "blocked",
      headerName: "Blocked",
      width: 90,
      valueFormatter: (value) => (value ? "Yes" : "No"),
    },
    {
      field: "organization_id",
      headerName: "Organization",
      flex: 1,
      minWidth: 150,
      valueFormatter: (value) => organizationNames.get(value as string) ?? "N/A",
    },
    {
      field: "team_id",
      headerName: "Team",
      flex: 1,
      minWidth: 150,
      valueFormatter: (value) => (value ? teamNames.get(value as string) ?? "N/A" : "None"),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => {
        const user = params.row as AdminUser;
        return [
          <GridActionsCellItem
            icon={<EditIcon fontSize="small" />}
            label="Edit"
            onClick={() => openEditDialog(user)}
          />,
          <GridActionsCellItem
            icon={user.blocked ? <LockOpenIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
            label={user.blocked ? "Unblock" : "Block"}
            onClick={() => setBlockTarget(user)}
          />,
        ];
      },
    },
  ];

  return (
    <Stack gap={2} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ color: "white" }}
        >
          Add User
        </Button>
      </Stack>

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <DataGridPro
          rows={users}
          columns={columns}
          filterMode="client"
          disableMultipleColumnsSorting={false}
          disableMultipleColumnsFiltering={false}
          sx={{ border: 0 }}
          loading={loading}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              required
              disabled={editing !== null}
              fullWidth
            />
            {!editing && (
              <TextField
                label="Temporary Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="Must satisfy the Auth0 password policy. Share it with the user out of band."
                size="small"
                required
                fullWidth
              />
            )}
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              size="small"
              required
              fullWidth
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              size="small"
              required
              fullWidth
            />
            <TextField
              label="Organization"
              value={organizationId}
              onChange={(e) => handleOrganizationChange(e.target.value)}
              size="small"
              select
              required
              fullWidth
            >
              {organizations.map((organization) => (
                <MenuItem key={organization.id} value={organization.id}>{organization.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              size="small"
              select
              disabled={organizationId === ""}
              fullWidth
            >
              <MenuItem value={NO_TEAM}>None</MenuItem>
              {organizationTeams.map((team) => (
                <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                />
              }
              label="Team Admin"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={canExtractPdf}
                  onChange={(e) => setCanExtractPdf(e.target.checked)}
                />
              }
              label="PDF Import"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ color: "white" }} disabled={!canSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        title={blockTarget?.blocked ? "Unblock User" : "Block User"}
        description={blockTarget?.blocked
          ? `Are you sure you want to unblock "${blockTarget?.first_name} ${blockTarget?.last_name}"? They will be able to sign in again.`
          : `Are you sure you want to block "${blockTarget?.first_name} ${blockTarget?.last_name}"? They will no longer be able to sign in.`}
        open={blockTarget !== null}
        onClose={() => setBlockTarget(null)}
        onAccept={handleBlockToggle}
        acceptLabel={blockTarget?.blocked ? "Unblock" : "Block"}
        acceptColor={blockTarget?.blocked ? "primary" : "error"}
      />

      <Snackbar
        open={snackMessage !== undefined}
        autoHideDuration={6000}
        onClose={() => setSnackMessage(undefined)}
        message={snackMessage}
      />
    </Stack>
  );
}

export default UsersPage;
